import {
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Portal,
  Tooltip,
} from '@chakra-ui/react';
import { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { defaultIconSize, Icons } from '../Icons';
import { resolveColumnId } from './useColumnVisibility';

interface ColumnToggleMenuProps {
  columns: ColumnDef<any, any>[];
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (visibility: VisibilityState) => void;
}

export function ColumnToggleMenu({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
}: ColumnToggleMenuProps) {
  const { t } = useTranslation();

  const toggleableColumns = columns.filter(
    (col) => (col.meta as any)?.toggleable,
  );

  if (toggleableColumns.length === 0) return null;

  const visibleIds = toggleableColumns
    .map((col) => {
      const id = resolveColumnId(col);
      return id && columnVisibility[id] !== false ? id : null;
    })
    .filter(Boolean) as string[];

  const handleChange = (values: string | string[]) => {
    const selected = Array.isArray(values) ? values : [values];
    const updated: VisibilityState = { ...columnVisibility };
    for (const col of toggleableColumns) {
      const id = resolveColumnId(col);
      if (!id) continue;
      updated[id] = selected.includes(id);
    }
    onColumnVisibilityChange(updated);
  };

  return (
    <Menu closeOnSelect={false} isLazy={true}>
      <Tooltip label={t('general.toggleColumns')}>
        <MenuButton
          opacity={0}
          _groupHover={{ opacity: 1 }}
          as={IconButton}
          icon={<Icons.FilterCog boxSize={defaultIconSize} />}
          size="sm"
          aria-label={t('general.toggleColumns')}
        />
      </Tooltip>
      <Portal>
        <MenuList zIndex="popover" maxH="400px" overflowY="auto">
          <MenuOptionGroup
            type="checkbox"
            value={visibleIds}
            onChange={handleChange}
          >
            {toggleableColumns.map((col) => {
              const id = resolveColumnId(col);
              const header = typeof col.header === 'string' ? col.header : id;
              return (
                <MenuItemOption key={id} value={id}>
                  {header}
                </MenuItemOption>
              );
            })}
          </MenuOptionGroup>
        </MenuList>
      </Portal>
    </Menu>
  );
}
